"use client";

import { Button, Field, HStack, Stack, Text, Textarea } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useCallback, useState } from "react";
import { type Origin } from "@/generated/fastapi";

import { showErrorToast, showWarningToast } from "@/shared/feedback";
import appSettings from "@/shared/config/appSettings";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import GenericSegment from "@/shared/ui/controls/GenericSegment";
import useOriginActions from "../hooks/useOriginActions";
import type { ExtendableOriginInput, OriginEntryMode } from "../types";
import OriginAudioFileCreateFlow from "./OriginAudioFileCreateFlow";
import OriginVoiceCreateFlow from "./OriginVoiceCreateFlow";

interface OriginExtendFormValues {
  additionalContent: string;
}

interface OriginExtendFormProps {
  originToExtend: ExtendableOriginInput;
  onCancel?: () => void;
  onSuccess?: (origin: Origin) => void;
  onError?: (error: unknown) => void;
  showOrientation?: boolean;
}

function formatEntryModeLabel(entryMode: OriginEntryMode) {
  if (entryMode === "audioFile") return "Audio file";
  return entryMode.charAt(0).toUpperCase() + entryMode.slice(1);
}

function formatOriginPreview(rawTextInput: string) {
  const normalized = rawTextInput.replace(/\s+/g, " ").trim();
  if (!normalized) return "Empty source material";
  return normalized.length > 96 ? `${normalized.slice(0, 96)}...` : normalized;
}

export default function OriginExtendForm({
  originToExtend,
  onCancel,
  onSuccess,
  onError,
  showOrientation = true,
}: OriginExtendFormProps) {
  const [entryMode, setEntryMode] = useState<OriginEntryMode>("text");
  const [audioFlowResetKey, setAudioFlowResetKey] = useState(0);
  const [isInstantSubmitting, setIsInstantSubmitting] = useState(false);
  const { handleUpdateOriginClick } = useOriginActions();
  const {
    clearErrors,
    register,
    handleSubmit,
    reset,
    resetField,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OriginExtendFormValues>({
    defaultValues: {
      additionalContent: "",
    },
  });
  const isVoiceMode = entryMode === "voice";
  const isAudioFileMode = entryMode === "audioFile";
  const additionalContentValue = watch("additionalContent");
  const cleanedAdditionalContent = additionalContentValue.trim();
  const isContentTooShort =
    cleanedAdditionalContent.length > 0 &&
    cleanedAdditionalContent.length < appSettings.validation.ORIGIN_MIN_LENGTH;
  const hasAdditionalContent = additionalContentValue.length > 0;
  const isExtendDisabled =
    isSubmitting ||
    isInstantSubmitting ||
    cleanedAdditionalContent.length < appSettings.validation.ORIGIN_MIN_LENGTH;
  const isClearDisabled =
    isSubmitting || isInstantSubmitting || !hasAdditionalContent;
  const disabledReason =
    cleanedAdditionalContent.length === 0
      ? isVoiceMode || isAudioFileMode
        ? "Record or upload audio, then review the transcript before extending."
        : "Add new source material before extending."
      : isContentTooShort
        ? `Add at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters.`
        : null;

  const resetAudioDraftState = useCallback(() => {
    resetField("additionalContent", { defaultValue: "" });
    clearErrors("additionalContent");
    setAudioFlowResetKey((current) => current + 1);
  }, [clearErrors, resetField]);

  const handleEntryModeChange = useCallback(
    (nextMode: OriginEntryMode) => {
      setEntryMode(nextMode);
      resetAudioDraftState();
    },
    [resetAudioDraftState],
  );
  const handleClearDraft = useCallback(() => {
    resetAudioDraftState();
  }, [resetAudioDraftState]);

  const submitExtension = useCallback(
    async (additionalContent: string): Promise<boolean> => {
      try {
        const cleanedInput = additionalContent.trim();

        if (!cleanedInput) {
          showWarningToast("No content to extend");
          return false;
        }

        if (cleanedInput.length < appSettings.validation.ORIGIN_MIN_LENGTH) {
          showWarningToast(
            `Content must be at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters`,
          );
          return false;
        }

        const updatedOrigin = await handleUpdateOriginClick(
          {
            origin_id: originToExtend.id,
            additional_text_input: cleanedInput,
          },
          (origin) => {
            onSuccess?.(origin);
            return origin;
          },
        );

        if (!updatedOrigin) {
          throw new Error("Extend failed");
        }

        reset();
        if (isVoiceMode || isAudioFileMode) {
          resetAudioDraftState();
        }

        return true;
      } catch (error) {
        showErrorToast("Failed to extend source material");
        onError?.(error);
        return false;
      }
    },
    [
      handleUpdateOriginClick,
      isAudioFileMode,
      isVoiceMode,
      onError,
      onSuccess,
      originToExtend.id,
      reset,
      resetAudioDraftState,
    ],
  );

  const handleTranscriptSubmit = useCallback(
    async (nextTranscript: string) => {
      const trimmedTranscript = nextTranscript.trim();

      setIsInstantSubmitting(true);
      const submitted = await submitExtension(trimmedTranscript);

      if (!submitted && trimmedTranscript) {
        setValue("additionalContent", trimmedTranscript, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setIsInstantSubmitting(false);
    },
    [setValue, submitExtension],
  );

  const onSubmit = handleSubmit(async (data) => {
    await submitExtension(data.additionalContent);
  });

  return (
    <form onSubmit={onSubmit} style={{ width: "100%" }}>
      <Stack gap="4" align="stretch" width="full">
        {showOrientation ? (
          <GenericDrawerInfo
            description="Add more source material to this existing source. The new text is appended as an additional section."
            items={[
              {
                label: originToExtend.language?.toUpperCase() ?? "No language",
                colorPalette: originToExtend.language ? "teal" : "gray",
              },
              { label: formatEntryModeLabel(entryMode), colorPalette: "blue" },
              {
                label: originToExtend.latest_at
                  ? `Updated ${originToExtend.latest_at.slice(0, 10)}`
                  : "Existing source",
                colorPalette: "gray",
              },
            ]}
          >
            <Text fontSize="xs" color="moveon.text.muted" lineClamp={2}>
              {formatOriginPreview(originToExtend.raw_text_input)}
            </Text>
          </GenericDrawerInfo>
        ) : null}

        <Field.Root>
          <Field.Label>Input mode</Field.Label>
          <GenericSegment<OriginEntryMode>
            activeState={entryMode}
            setActiveState={handleEntryModeChange}
            options={[
              { value: "text", label: "Text" },
              { value: "voice", label: "Voice" },
              { value: "audioFile", label: "Audio file" },
            ]}
            size="sm"
            disabled={isSubmitting || isInstantSubmitting}
          />
          <Field.HelperText>
            Choose whether to type the extension yourself or record it first.
          </Field.HelperText>
        </Field.Root>

        {isVoiceMode && (
          <OriginVoiceCreateFlow
            key={`extend-voice-${audioFlowResetKey}`}
            isSubmittingTranscript={isInstantSubmitting}
            readyMessage="Transcript received. You can edit it below before extending the source material."
            sourcedWhere="backlog"
            transcribeAndSubmitLabel="Transcribe & extend"
            transcriptValue={additionalContentValue}
            onTranscriptChange={(nextTranscript) => {
              setValue("additionalContent", nextTranscript, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            onTranscriptReset={() => {
              resetField("additionalContent", { defaultValue: "" });
              clearErrors("additionalContent");
            }}
            onTranscriptSubmit={handleTranscriptSubmit}
          />
        )}

        {isAudioFileMode && (
          <OriginAudioFileCreateFlow
            key={`extend-audio-file-${audioFlowResetKey}`}
            isSubmittingTranscript={isInstantSubmitting}
            readyMessage="Transcript received. You can edit it below before extending the source material."
            sourcedWhere="backlog"
            transcribeAndSubmitLabel="Transcribe & extend"
            transcriptValue={additionalContentValue}
            onTranscriptChange={(nextTranscript) => {
              setValue("additionalContent", nextTranscript, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            onTranscriptReset={() => {
              resetField("additionalContent", { defaultValue: "" });
              clearErrors("additionalContent");
            }}
            onTranscriptSubmit={handleTranscriptSubmit}
          />
        )}

        <Field.Root invalid={!!errors.additionalContent} width="full">
          <Field.Label>
            {isVoiceMode || isAudioFileMode ? "Transcript" : "Extend with"}
          </Field.Label>
          {disabledReason ? (
            <Text mb="2" fontSize="sm" color="moveon.text.muted">
              {disabledReason}
            </Text>
          ) : null}
          <Textarea
            autoresize
            minH="3xs"
            maxH="3xs"
            placeholder={
              isVoiceMode || isAudioFileMode
                ? "Your transcribed recording will appear here for editing..."
                : "Add the next part of this source material..."
            }
            {...register("additionalContent", {
              required: "Must provide content to extend the source material",
              minLength: {
                value: appSettings.validation.ORIGIN_MIN_LENGTH,
                message: `Content must be at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters`,
              },
            })}
          />
          <Field.HelperText>
            {isVoiceMode || isAudioFileMode
              ? "Review and edit the transcript before extending the source material."
              : "Your new text will be appended as an additional section."}
          </Field.HelperText>
          <Field.ErrorText>{errors.additionalContent?.message}</Field.ErrorText>
        </Field.Root>

        <HStack gap="2" align="center" justify="space-between" width="full">
          <HStack gap="2" justify="flex-start">
            {onCancel ? (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                aria-label="Cancel extending"
                minW={{ base: "10", sm: "auto" }}
              >
                <GenericIcon icon="return" />
                <Text as="span" display={{ base: "none", sm: "inline" }}>
                  Return
                </Text>
              </Button>
            ) : null}

            <Button
              type="button"
              variant="outline"
              color="moveon.warning"
              borderColor="moveon.warning"
              _hover={{ bg: "moveon.warningLight" }}
              onClick={handleClearDraft}
              disabled={isClearDisabled}
              aria-label="Clear extension draft"
              minW={{ base: "10", sm: "auto" }}
            >
              <GenericIcon icon="reset" />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Clear
              </Text>
            </Button>
          </HStack>

          <HStack gap="2" justify="flex-end">
            <Button
              type="submit"
              bg="moveon.primary"
              loading={isSubmitting || isInstantSubmitting}
              loadingText="Extending"
              disabled={isExtendDisabled}
              aria-label="Extend source material"
              minW={{ base: "10", sm: "auto" }}
            >
              <GenericIcon icon="plus" />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Extend
              </Text>
            </Button>
          </HStack>
        </HStack>
      </Stack>
    </form>
  );
}
